export type QuizOption = {
  text: string
  points: number
}

export type QuizQuestion = {
  id: string
  title: string
  subtitle: string
  options: QuizOption[]
  weight: number
}

export type Archetype = {
  id: string
  name: string
  rawMin: number
  rawMax: number
  scoreMin: number
  scoreMax: number
  color: string
  headline: string
  identity: string
  bodyLines: string[]
  doingWell: { intro?: string; items: string[]; outro?: string }
  blindSpots: {
    title: string
    intro?: string
    items: string[]
    outro?: string
  }
  confidentDiff: {
    intro: string
    items: string[]
    outro?: string
  }
  nextStep: {
    intro: string
    items?: string[]
    cta: string
    ctaLabel: string
  }
  traits: string[]
  messagingAngle: string
  badgeScore: number
}

export const QUIZ_TITLE = 'What\u2019s Your Home Maintenance Personality?'
export const QUIZ_SUBTITLE = 'Discover your Hudson Home Archetype'

export const QUESTIONS: QuizQuestion[] = [
  {
    id: 'q1',
    title: 'Problem Response Style',
    subtitle: 'When something unexpected happens in your home, you usually:',
    weight: 1,
    options: [
      { text: 'It rarely surprises me because I track systems', points: 5 },
      { text: 'Research before deciding', points: 4 },
      { text: 'Fix it quickly and move on', points: 3 },
      { text: 'Wait until it becomes urgent', points: 2 },
      { text: 'Feel unsure where to start', points: 1 },
    ],
  },
  {
    id: 'q2',
    title: 'Maintenance Tracking',
    subtitle: 'How do you keep track of maintenance tasks?',
    weight: 1,
    options: [
      { text: 'Systems or automated reminders', points: 5 },
      { text: 'Calendar or notes', points: 4 },
      { text: 'Mental checklist', points: 3 },
      { text: 'I handle things when they come up', points: 2 },
      { text: "I don\u2019t track it", points: 1 },
    ],
  },
  {
    id: 'q3',
    title: 'Planning Horizon',
    subtitle: 'How far ahead do you think about home updates or maintenance?',
    weight: 1,
    options: [
      { text: '2+ years ahead', points: 5 },
      { text: 'About a year', points: 4 },
      { text: 'A few months', points: 3 },
      { text: 'Mostly reactive', points: 2 },
      { text: 'I try not to think about it', points: 1 },
    ],
  },
  {
    id: 'q4',
    title: 'Mental Load',
    subtitle: 'How often do you feel like you might be forgetting something about your home?',
    weight: 2,
    options: [
      { text: 'Rarely', points: 5 },
      { text: 'Occasionally', points: 4 },
      { text: 'Often', points: 3 },
      { text: 'Constantly', points: 2 },
      { text: 'I usually avoid thinking about it', points: 1 },
    ],
  },
  {
    id: 'q5',
    title: 'Confidence Level',
    subtitle: 'How confident are you that your home is protected from costly surprises?',
    weight: 1,
    options: [
      { text: 'Very confident', points: 5 },
      { text: 'Mostly confident', points: 4 },
      { text: 'Unsure', points: 3 },
      { text: 'Not confident', points: 2 },
      { text: 'I honestly have no idea', points: 1 },
    ],
  },
  {
    id: 'q6',
    title: 'Time Investment',
    subtitle: 'How much time do you spend managing home maintenance?',
    weight: 1,
    options: [
      { text: 'Minimal \u2014 systems handle it', points: 5 },
      { text: 'A few hours monthly', points: 4 },
      { text: 'Several weekends a year', points: 3 },
      { text: 'I handle problems as they appear', points: 2 },
      { text: 'More than I want / It stresses me out', points: 1 },
    ],
  },
  {
    id: 'q7',
    title: 'Awareness',
    subtitle: 'Do you know the expected lifespan of major systems (roof, HVAC, water heater)?',
    weight: 1,
    options: [
      { text: 'Yes \u2014 clearly', points: 5 },
      { text: 'Roughly', points: 4 },
      { text: 'Somewhat', points: 3 },
      { text: 'Not really', points: 2 },
      { text: 'I never thought about it', points: 1 },
    ],
  },
  {
    id: 'q8',
    title: 'Emotional Relationship',
    subtitle: 'Which statement best describes your relationship with your home?',
    weight: 1,
    options: [
      { text: 'My home runs smoothly with minimal surprises', points: 5 },
      { text: 'I try to stay ahead of issues', points: 4 },
      { text: 'I manage things as they come up', points: 3 },
      { text: 'I sometimes feel behind', points: 2 },
      { text: 'My home feels unpredictable', points: 1 },
    ],
  },
]

export const MAX_RAW_SCORE = 45
export const MIN_RAW_SCORE = 9

export function calculateRawScore(answers: Record<string, number>): number {
  return QUESTIONS.reduce((sum, q) => {
    const pts = answers[q.id] ?? 0
    return sum + pts * q.weight
  }, 0)
}

export function calculateProtectionScore(rawScore: number): number {
  return Math.round((rawScore / MAX_RAW_SCORE) * 100)
}

export const SCORE_ZONES = [
  { label: 'High Risk', min: 0, max: 35, color: '#DC2626' },
  { label: 'Reactive', min: 36, max: 50, color: '#F59E0B' },
  { label: 'Juggling', min: 51, max: 68, color: '#D97706' },
  { label: 'Strategic', min: 69, max: 83, color: '#145F6B' },
  { label: 'Confident', min: 84, max: 100, color: '#059669' },
] as const

export function getScoreZone(protectionScore: number) {
  return SCORE_ZONES.find(z => protectionScore >= z.min && protectionScore <= z.max) ?? SCORE_ZONES[0]
}

export const ARCHETYPES: Archetype[] = [
  {
    id: 'quiet-avoider',
    name: 'Quiet Avoider',
    rawMin: 9,
    rawMax: 15,
    scoreMin: 20,
    scoreMax: 35,
    color: '#DC2626',
    headline: 'Your Home Maintenance Personality:',
    identity: 'Quiet Avoider',
    bodyLines: [
      'You care about your home, but the complexity of maintenance can make it hard to know where to begin.',
      "Things don\u2019t feel \u201Cbroken enough\u201D to act on\u2026 but not handled enough to feel in control.",
      'So tasks get pushed. Decisions get delayed. And the list quietly grows in the background.',
    ],
    doingWell: {
      intro: "Even if it doesn\u2019t feel like it \u2014 you are not behind. You\u2019re at the starting point most homeowners never define.",
      items: [
        'Understand your home needs attention (many ignore it entirely)',
        'Are open to better systems instead of guessing',
        'You care about protecting your investment',
      ],
      outro: 'That awareness is what separates you from staying stuck.',
    },
    blindSpots: {
      title: 'Blind Spots',
      intro: 'Quiet Avoiders often experience:',
      items: [
        'Uncertainty about what needs attention first',
        'Concern about unexpected repair costs',
        'Difficulty prioritizing maintenance tasks',
      ],
      outro: 'Without a clear roadmap, small issues can feel larger than they are.',
    },
    confidentDiff: {
      intro: "Confident Protectors don\u2019t know more about homes than everyone else. They simply have:",
      items: [
        'A clear maintenance roadmap',
        'Visibility into upcoming system replacements',
        'A proactive plan instead of reactive decisions',
      ],
    },
    nextStep: {
      intro: 'The fastest way to move from overwhelmed to confident is understanding exactly where your home stands today. Hudson My Home provides a complete Home Assessment that identifies:',
      items: [
        'A full maintenance overview',
        'System lifespan visibility',
        'A prioritized roadmap for your home',
      ],
      cta: '/membership',
      ctaLabel: 'Book Your Home Health Assessment',
    },
    traits: ['Busy life', 'Avoidance due to complexity', 'Overwhelmed by decisions'],
    messagingAngle: 'Clarity removes overwhelm.',
    badgeScore: 26,
  },
  {
    id: 'on-call-operator',
    name: 'On-Call Operator',
    rawMin: 16,
    rawMax: 22,
    scoreMin: 36,
    scoreMax: 50,
    color: '#F59E0B',
    headline: 'Your Home Maintenance Personality:',
    identity: 'On-Call Operator',
    bodyLines: [
      "You\u2019re capable and responsive when something needs attention. You handle issues as they arise and are comfortable solving problems in the moment.",
      'The challenge is that reactive maintenance can still create surprises.',
    ],
    doingWell: {
      items: [
        'You respond quickly to problems',
        "You don\u2019t ignore visible issues",
        "You\u2019re willing to take action when something needs attention",
      ],
      outro: 'These are strong homeowner traits.',
    },
    blindSpots: {
      title: 'Where Stress Usually Shows Up',
      intro: 'On-Call Operators often encounter:',
      items: [
        'Unexpected repair costs',
        'Short-notice maintenance needs',
        'Systems wearing out sooner than expected',
      ],
      outro: 'This happens because many maintenance issues develop before they become visible.',
    },
    confidentDiff: {
      intro: 'Confident Protectors shift from reacting to predicting. They rely on:',
      items: [
        'Preventative inspections',
        'Maintenance schedules',
        'System replacement planning',
      ],
      outro: 'This prevents emergencies before they start.',
    },
    nextStep: {
      intro: 'The fastest way to move toward Confident Protector is understanding exactly where your home stands today. Hudson My Home provides a complete Home Assessment that identifies:',
      items: [
        'What systems are aging',
        'What maintenance is approaching',
        'Where proactive steps will prevent costly repairs',
      ],
      cta: '/membership',
      ctaLabel: 'Book Your Home Health Assessment',
    },
    traits: ['Maintenance happens after problems', 'High risk for costly surprises', 'Carries too much alone'],
    messagingAngle: "You don\u2019t need to do more \u2014 you need visibility before problems show up.",
    badgeScore: 44,
  },
  {
    id: 'responsible-juggler',
    name: 'Responsible Juggler',
    rawMin: 23,
    rawMax: 30,
    scoreMin: 51,
    scoreMax: 68,
    color: '#D97706',
    headline: 'Your Home Maintenance Personality:',
    identity: 'Responsible Juggler',
    bodyLines: [
      'You care deeply about maintaining your home and work hard to stay on top of things.',
      'Many homeowners at this stage manage maintenance through lists, reminders, and memory.',
      'But that effort can create a heavy mental load.',
    ],
    doingWell: {
      items: [
        'You actively try to stay ahead of issues',
        "You care about protecting your home\u2019s value",
        'You notice problems before they become emergencies',
      ],
      outro: 'That puts you ahead of many homeowners.',
    },
    blindSpots: {
      title: 'Where Stress Usually Shows Up',
      intro: 'Responsible Jugglers often experience:',
      items: [
        'Constant mental reminders about home tasks',
        'Uncertainty about future system replacements',
        'Feeling like something might be slipping through the cracks',
      ],
      outro: "It\u2019s not effort that\u2019s missing \u2014 it\u2019s systemization.",
    },
    confidentDiff: {
      intro: 'Confident Protectors shift the burden from memory to systems. They rely on:',
      items: [
        'A structured maintenance schedule',
        'Long-term home planning',
        'Proactive monitoring of systems',
      ],
      outro: 'The result is significantly less mental load.',
    },
    nextStep: {
      intro: 'Hudson helps Responsible Jugglers turn effort into clear maintenance systems. Our Home Health Assessment identifies:',
      items: [
        'What needs attention now',
        "What\u2019s coming next",
        'What can wait',
      ],
      cta: '/membership',
      ctaLabel: 'Book Your Home Health Assessment',
    },
    traits: ['Good intentions', 'Mental load heavy', 'Tasks slip through cracks'],
    messagingAngle: "You\u2019re doing everything right \u2014 except letting systems do the heavy lifting.",
    badgeScore: 62,
  },
  {
    id: 'intentional-planner',
    name: 'Intentional Planner',
    rawMin: 31,
    rawMax: 37,
    scoreMin: 69,
    scoreMax: 83,
    color: '#145F6B',
    headline: 'Your Home Maintenance Personality:',
    identity: 'Intentional Planner',
    bodyLines: [
      'You approach homeownership strategically and think ahead about maintenance, upgrades, and long-term value.',
      'You already understand that proactive planning prevents expensive surprises.',
    ],
    doingWell: {
      items: [
        'You think ahead about maintenance',
        'You value structure and planning',
        'You recognize the importance of proactive care',
      ],
      outro: 'These habits place you among the most prepared homeowners.',
    },
    blindSpots: {
      title: 'Where Opportunities Still Exist',
      intro: 'Intentional Planners often still manage maintenance themselves, which can create:',
      items: [
        'Time investment',
        'Coordination challenges',
        'Uncertainty about system health',
      ],
    },
    confidentDiff: {
      intro: 'Confident Protectors combine planning with professional maintenance systems. They rely on:',
      items: [
        'Regular inspections',
        'Preventative maintenance execution',
        'Long-term home strategy',
      ],
    },
    nextStep: {
      intro: 'Hudson partners with homeowners like you to transform planning into complete home confidence.',
      cta: '/membership',
      ctaLabel: 'Optimize Your Home Maintenance Strategy',
    },
    traits: ['Future-focused', 'Budget aware', 'Wants structured planning'],
    messagingAngle: "You\u2019re ready for full home strategy.",
    badgeScore: 75,
  },
  {
    id: 'confident-protector',
    name: 'Confident Protector',
    rawMin: 38,
    rawMax: 45,
    scoreMin: 84,
    scoreMax: 100,
    color: '#059669',
    headline: 'Your Home Maintenance Personality:',
    identity: 'Confident Protector',
    bodyLines: [
      'You approach homeownership with clarity and foresight.',
      'You understand that proactive maintenance protects both your home and your peace of mind.',
    ],
    doingWell: {
      items: [
        'You think proactively about your home',
        'You prioritize maintenance systems',
        'You value long-term home protection',
      ],
      outro: 'This mindset significantly reduces unexpected repair costs.',
    },
    blindSpots: {
      title: 'Where Hudson Can Help',
      intro: 'Even Confident Protectors benefit from expert support to ensure nothing is overlooked. Hudson helps elevate proactive homeowners through:',
      items: [
        'Detailed system tracking',
        'Preventative service scheduling',
        'Strategic home planning',
      ],
    },
    confidentDiff: {
      intro: 'You\u2019re already thinking like a Confident Protector. The next level is having professional systems behind you.',
      items: [],
    },
    nextStep: {
      intro: "Hudson can help maintain the confidence you\u2019ve already built.",
      cta: '/membership',
      ctaLabel: 'Book Your Home Health Assessment',
    },
    traits: ['Systems in place', 'Preventative mindset', 'Values expertise'],
    messagingAngle: "The goal isn\u2019t perfection \u2014 it\u2019s confidence.",
    badgeScore: 87,
  },
]

export function getArchetype(rawScore: number): Archetype {
  return (
    ARCHETYPES.find(a => rawScore >= a.rawMin && rawScore <= a.rawMax) ??
    ARCHETYPES[0]
  )
}

export const CONFIDENCE_GAP_LINE =
  'Most homeowners overestimate their confidence by 20\u201340%. The gap between how protected you feel and how protected you actually are is where stress lives.'

export const SHARE_PROMPT =
  'Share your home maintenance personality with a friend and see who\u2019s the Confident Protector.'
